import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useRanking } from '../hooks/useRanking';

interface ResultOverlayProps {
  $show: boolean;
}

const ResultOverlay = styled.div<ResultOverlayProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ResultPanel = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 40px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 20px;
  font-size: 2rem;
`;

const GameCompleteMessage = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 15px 20px;
  border-radius: 15px;
  margin-bottom: 20px;
  font-size: 1.1rem;
  text-align: center;
  font-weight: 600;
`;

const ScoreDisplay = styled.div`
  font-size: 3rem;
  font-weight: bold;
  color: #4CAF50;
  margin: 20px 0;
`;

const Stats = styled.div`
  margin: 20px 0;
  font-size: 1.1rem;
  color: #666;
`;

const Input = styled.input`
  flex: 1;
  padding: 12px;
  border: 2px solid #ddd;
  border-radius: 10px;
  font-size: 1rem;
  margin: 0;
  box-sizing: border-box;
  min-width: 0; // flex item 축소 방지
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const SaveButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin: 20px 0;
  gap: 10px;
  align-items: stretch;
  justify-content: flex-start;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

interface ButtonProps {
  primary?: boolean;
}

const Button = styled.button<ButtonProps>`
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.primary ? `
    background: #4CAF50;
    color: white;
    
    &:hover {
      background: #45a049;
    }
  ` : `
    background: #f0f0f0;
    color: #333;
    
    &:hover {
      background: #e0e0e0;
    }
  `}
`;

const SaveButton = styled(Button)`
  flex: 0 0 auto;
  min-width: 100px;
  height: 100%;
  white-space: nowrap;
`;

interface ResultPanelProps {
  show: boolean;
  reactionTime: number;
  score: number;
  onPlayAgain: () => void;
  onMainMenu: () => void;
}

const ResultPanelComponent: React.FC<ResultPanelProps> = ({
  show,
  reactionTime,
  score,
  onPlayAgain,
  onMainMenu,
}) => {
  const [playerName, setPlayerName] = useState('');
  const [saved, setSaved] = useState(false);
  const { savePlayerScore } = useRanking();

  const handleSaveScore = () => {
    if (playerName.trim()) {
      savePlayerScore(playerName, reactionTime, score);
      setSaved(true);
    }
  };

  const getPerformanceMessage = () => {
    if (reactionTime <= 200) return '⚡ 믿을 수 없는 속도!';
    if (reactionTime <= 300) return '🔥 매우 빠릅니다!';
    if (reactionTime <= 400) return '👍 좋은 속도입니다!';
    if (reactionTime <= 500) return '😊 괜찮은 속도입니다!';
    return '💪 더 연습해보세요!';
  };

  const handleClose = () => {
    setPlayerName('');
    setSaved(false);
    onMainMenu();
  };

  if (!show) return null;

  return (
    <ResultOverlay $show={show}>
      <ResultPanel
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Title>🏆 게임 완료!</Title>
        
        <GameCompleteMessage>
          5판 중 최고 반응속도: {reactionTime}ms
        </GameCompleteMessage>
        
        <ScoreDisplay>{score}점</ScoreDisplay>
        
        <Stats>
          <div>최소 반응시간: {reactionTime}ms</div>
          <div>총 점수: {score}점</div>
          <div style={{ marginTop: '10px' }}>{getPerformanceMessage()}</div>
        </Stats>

        {!saved ? (
          <>
            <SaveButtonContainer>
              <Input
                type="text"
                placeholder="이름을 입력하세요"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                maxLength={20}
              />
              
              <SaveButton primary onClick={handleSaveScore}>
                점수 저장
              </SaveButton>
            </SaveButtonContainer>
          </>
        ) : (
          <div style={{ color: '#4CAF50', fontSize: '1.2rem', margin: '20px 0' }}>
            ✓ 점수가 저장되었습니다!
          </div>
        )}

        <ButtonGroup>
          <Button onClick={handleClose}>
            홈
          </Button>
          <Button primary onClick={onPlayAgain}>
            다시 하기
          </Button>
        </ButtonGroup>
      </ResultPanel>
    </ResultOverlay>
  );
};

export default ResultPanelComponent;